import { Injector, runInInjectionContext } from '@angular/core';
import { ERMDatePipe, ERMDateTimePipe } from '@shared/pipes/erm-date.pipe';
import {
  convertKeysToCamelCase,
  ExtractValueFromPathPipe,
  GridColumn,
  GridColumns,
} from '@wcg/frontend-framework';

export function transformGridDateFormat<T extends Record<string, unknown>>(
  injector: Injector,
  data: T[],
  columns: GridColumns,
): T[] {
  if (!data?.length || !columns) return data;

  return runInInjectionContext(injector, () => {
    const ermDatePipe = injector.get(ERMDatePipe);
    const ermDateTimePipe = injector.get(ERMDateTimePipe);
    const extractValueFromPath = injector.get(ExtractValueFromPathPipe);

    const gridColumns = Object.values(
      convertKeysToCamelCase(columns),
    ) as GridColumn[];

    const dateColumns = gridColumns.filter(
      (col): col is GridColumn =>
        col.componentType === 'date' || col.componentType === 'datetime',
    );

    if (!dateColumns.length) return data;

    return data.map((row) => {
      const updatedRow: T = { ...row };

      dateColumns.forEach((col) => {
        const path = col.propKey;
        const value = extractValueFromPath.transform(row, path) as unknown;

        if (isInvalidDate(value)) {
          setValueByPath(updatedRow, path, '');
          return;
        }

        if (value === 'N/A') {
          setValueByPath(updatedRow, path, value);
          return;
        }

        const pipe =
          col.componentType === 'date' ? ermDatePipe : ermDateTimePipe;

        setValueByPath(updatedRow, path, pipe.transform(value as string));
      });

      return updatedRow;
    });
  });
}

export function isInvalidDate(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === 'None' ||
    value === 'empty' ||
    value === ''
  );
}

export function setValueByPath<T extends Record<string, unknown>>(
  obj: T,
  path: string,
  value: unknown,
): void {
  const keys = path.split('.');
  const lastKey = keys.pop();

  if (!lastKey) return;

  let current: Record<string, unknown> = obj;

  for (const key of keys) {
    const next = current[key];

    if (typeof next !== 'object' || next === null) {
      current[key] = {};
    }

    current = current[key] as Record<string, unknown>;
  }

  current[lastKey] = value;
}
